import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export interface Profile {
  id: string;
  user_id: string;
  full_name: string;
  company: string;
  phone: string;
  approval_status: string;
  avatar_url: string;
  default_customer_markup_type: string;
  default_customer_markup_value: number;
}

export function useProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const profileQuery = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user!.id)
        .single();
      if (error) throw error;
      const profile = data as unknown as Profile;

      // avatar_url stores the storage path (e.g. "<uid>/avatar.png") for the
      // now-private `avatars` bucket. Mint a short-lived signed URL for
      // display; legacy rows with a full http(s) URL are still accepted.
      if (profile.avatar_url && !/^https?:\/\//i.test(profile.avatar_url)) {
        const { data: signed } = await supabase.storage
          .from('avatars')
          .createSignedUrl(profile.avatar_url, 60 * 60);
        if (signed?.signedUrl) {
          profile.avatar_url = signed.signedUrl;
        }
      }
      return profile;
    },
    enabled: !!user,
  });

  const updateProfile = useMutation({
    mutationFn: async (updates: { full_name: string; company?: string; phone: string; default_customer_markup_type?: string; default_customer_markup_value?: number }) => {
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('user_id', user.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });

  const uploadAvatar = useMutation({
    mutationFn: async (file: File) => {
      if (!user) throw new Error('Not authenticated');
      const ext = file.name.split('.').pop();
      const filePath = `${user.id}/avatar.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });
      if (uploadError) throw uploadError;

      // Bucket is private — persist the object path, not a public URL.
      // Consumers mint a signed URL on read (see profileQuery above).
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: filePath } as any)
        .eq('user_id', user.id);
      if (updateError) throw updateError;

      const { data: signed } = await supabase.storage
        .from('avatars')
        .createSignedUrl(filePath, 60 * 60);
      return signed?.signedUrl ?? filePath;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });

  return { profile: profileQuery.data, isLoading: profileQuery.isLoading, updateProfile, uploadAvatar };
}
